import { Injectable } from '@angular/core';
import { LINE_OF_BUSINESS_ENUM } from '@ptl/account-management';
import { Brand, BrandingConfigurationService, brandValues, KyruusConfig } from '@ptl/brand';
// import { Brand, BrandingConfigurationService } from '@ptl/brand';
import moment from 'moment';
import { Person, RatingInfoPerson } from 'src/app/angular-elements/form-wrappers/form-components/individual-market-persons/interfaces/person.interface';
import { IndividualMarketStateStructure, RatingInfo } from 'src/app/angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/interfaces/individualMarket.interfaceStateStructure';
import { PersonalInfoStateStructure } from 'src/app/angular-elements/shared/interfaces/personalInfoStateStructure.interface';
import { MomentTimeZoneEnum } from 'src/app/shared/enums/moment-timezone.enum';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { environment } from 'src/environments/environment';
import { AuthorizedMember, IS_PRIMARY_APPLICANT, MemberData, PrimaryMember } from '../../configs/interfaces/authorization/payload.request';

@Injectable({
  providedIn: 'root'
})
export class MemberDataService {

  queryBrandCode: string;
  lastZipCode: string;

  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private brandService: BrandingConfigurationService
  ) { }
  

  private getDateOfBirth(person: Person | PersonalInfoStateStructure) {
    const DATE_FORMAT = "YYYY-MM-DD";
    if (!person.dob) { 
      console.error("Missing DOB from person.");
      throw new Error("Missing DOB from person");
    }

    /** DOB is currently stored as UTC, and therefore is exempt from timezone related config */
    // const momentDob = moment(person.dob).tz(MomentTimeZoneEnum[environment.configs.siteTimeZone]);
    const momentDob = moment(person.dob).utc(); 

    const formattedDate = momentDob.format(DATE_FORMAT);
    return formattedDate;
  }

  public async getMemberData(lineOfBusiness: LINE_OF_BUSINESS_ENUM, zipCode: string): Promise<MemberData> {

    let subscribers = [];
    let nonSubscribers = []

    let defaultConfigs = {
      "insurerCode": "FINDCARE_I",
      "memberId": "public_user",
      "userType": "member"
    };

    const brand: Brand = this.brandService.getCurrentBrand();
    const brandCode = brand.vendorUse?.kyruus[lineOfBusiness].genericCookieBrandCode?.toUpperCase();
    let universalConfigs = {
      brandCode
    };

    /** For individual markets */
    if (lineOfBusiness === LINE_OF_BUSINESS_ENUM.INDIVIDUAL) {
      const individualMarketState: IndividualMarketStateStructure = this.reducerUtilityService.getPayloadState('individualMarket');
      const ratingInfo: RatingInfo = individualMarketState.ratingInfo;

      const subscriberPersons: RatingInfoPerson = ratingInfo.subscriber;
      const subscriberPerson: Person = subscriberPersons && subscriberPersons[0];
      let subscriber: PrimaryMember = {
        ...defaultConfigs,
        ...universalConfigs,
        dob: this.getDateOfBirth(subscriberPerson),
        gender: subscriberPerson.gender,
        firstName: subscriberPerson.firstName || "placeholder_firstName",
        middleName: subscriberPerson.middleInitial || "placeholder_middleInitial",
        lastName: subscriberPerson.lastName || "placeholder_lastName"
      };
      subscribers.push(subscriber);

      const spousalPartnerPersons: RatingInfoPerson = ratingInfo['spousal-partner'];
      const spousalPartner: Person = spousalPartnerPersons && spousalPartnerPersons[0];
      if (spousalPartner) {
        let spouse: PrimaryMember = {
          ...defaultConfigs,
          ...universalConfigs,
          dob: this.getDateOfBirth(spousalPartner),
          gender: spousalPartner.gender,
          firstName: spousalPartner.firstName || "placeholder_firstName",
          middleName: spousalPartner.middleInitial || "placeholder_middleInitial",
          lastName: spousalPartner.lastName || "placeholder_lastName"
        };
        nonSubscribers.push(spouse);
      }

      const dependents: RatingInfoPerson = ratingInfo.dependents;
      if (dependents) {
        Object.entries(dependents).forEach( ([key, dependent]) => {
          const dependentPerson: PrimaryMember = {
            ...defaultConfigs,
            ...universalConfigs,
            dob: this.getDateOfBirth(dependent),
            gender: dependent.gender,
            firstName: dependent.firstName || "placeholder_firstName",
            middleName: dependent.middleInitial || "placeholder_middleInitial",
            lastName: dependent.lastName || "placeholder_lastName"
          };
          nonSubscribers.push(dependentPerson);
        });
      }

    } else if (lineOfBusiness === LINE_OF_BUSINESS_ENUM.SENIOR) {
      const payloadStates: { personalInfo: PersonalInfoStateStructure } = this.reducerUtilityService.getPayloadStates();
      const personalInfo = payloadStates.personalInfo;

      let subscriber: PrimaryMember = {
        ...defaultConfigs,
        ...universalConfigs,
        dob: personalInfo.dob ? this.getDateOfBirth(personalInfo) : "1950-01-01", // ,
        gender: personalInfo.gender || "F",
        firstName: personalInfo.firstName || "placeholder_firstName",
        middleName: personalInfo.middleInitial || "placeholder_middleInitial",
        lastName: personalInfo.lastName || "placeholder_lastName"
      };
      subscribers.push(subscriber);
    }

    const defaultMember: { default: IS_PRIMARY_APPLICANT } = { default: IS_PRIMARY_APPLICANT.YES };
    const authorizedMembers: AuthorizedMember[] = [
      { ...subscribers[0], ...defaultMember }, // Subscriber is flagged as default
      ...nonSubscribers
    ];
    
    const memberData: MemberData = {
      "primaryMember": subscribers[0],
      "authorizedMembers": authorizedMembers,
      "tripleKey": "sp_highmark|idp_hs_highmark|public_user"
    }

    return memberData;
  }

}
